import { useState } from "react";
import { useGetProjectsQuery } from '../../store/api/services'

const TAG_COLORS = [
  "bg-amber-500/20 text-amber-300 border-amber-500/30",
  "bg-blue-500/20 text-blue-300 border-blue-500/30",
  "bg-red-500/20 text-red-300 border-red-500/30",
  "bg-purple-500/20 text-purple-300 border-purple-500/30",
  "bg-green-500/20 text-green-300 border-green-500/30",
  "bg-orange-500/20 text-orange-300 border-orange-500/30",
  "bg-teal-500/20 text-teal-300 border-teal-500/30",
  "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  "bg-pink-500/20 text-pink-300 border-pink-500/30",
  "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
]

// Deterministic color per tag name
function tagColor(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length]
}

// Display labels for filter tabs and card tags (filtering still uses the original tag name)
const FILTER_LABELS = {
  "All Projects": "Completed Projects",
  "Restoration": "Paint Restoration",
  "Engine SwapEngine Swap": "Performance Upgrades",
}

function resolveImage(url) {
  if (!url) return null
  if (url.startsWith('http')) return url
  return `${process.env.REACT_APP_API_URL}${url}`
}

export default function ProjectsSection() {
  const [active, setActive]   = useState("All Projects")
  const [hovered, setHovered] = useState(null)

  const { data: projects = [], isLoading, isError } = useGetProjectsQuery()

  // Build filter tabs from unique tag names + "All Projects"
  const allTags   = [...new Set(projects.flatMap(p => (p.tag_details || []).map(t => t.name)))]
  const filters   = ["All Projects", ...allTags]

  const filtered  = active === "All Projects"
    ? projects
    : projects.filter(p => (p.tag_details || []).some(t => t.name === active))

  return (
    <section className="min-h-screen bg-[#1A1A1A] px-6 py-12 font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700&family=Barlow:wght@400;500&display=swap');
        .proj-section * { font-family: 'Barlow', sans-serif; }
        .proj-title { font-family: 'Barlow Condensed', sans-serif; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .card-enter { animation: fadeUp 0.4s ease forwards; }
        .img-zoom img { transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
        .img-zoom:hover img { transform: scale(1.06); }
      `}</style>

      <div className="proj-section max-w-6xl mx-auto">

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              className={`px-5 py-2 rounded-sm text-sm font-semibold tracking-wide border transition-all duration-200 ${
                active === f
                  ? "bg-amber-500 text-black border-amber-500 shadow-lg shadow-amber-500/20"
                  : "bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500 hover:text-zinc-200"
              }`}
            >
              {FILTER_LABELS[f] || f}
            </button>
          ))}
        </div>

        {/* States */}
        {isLoading && (
          <p className="text-zinc-500 text-center py-20">Loading projects...</p>
        )}
        {isError && (
          <p className="text-red-400 text-center py-20">Failed to load projects.</p>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <p className="text-zinc-500 text-center py-20">No projects found.</p>
        )}

        {/* Grid */}
        {!isLoading && !isError && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {filtered.map((project, i) => {
              const img = resolveImage(project.image)
              return (
                <div
                  key={project.id}
                  className="card-enter group bg-zinc-900 overflow-hidden"
                  style={{ animationDelay: `${i * 0.07}s`, opacity: 0 }}
                  onMouseEnter={() => setHovered(project.id)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden img-zoom" style={{ height: 240 }}>
                    {img ? (
                      <img src={img} alt={project.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-600 text-5xl">
                        🖼️
                      </div>
                    )}

                    {/* Gradient overlay */}
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)" }}
                    />

                    {/* Hover darkening */}
                    <div
                      className="absolute inset-0 bg-black transition-opacity duration-300"
                      style={{ opacity: hovered === project.id ? 0.15 : 0 }}
                    />

                    {/* Tag pills */}
                    <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
                      {(project.tag_details || []).map(tag => (
                        <span
                          key={tag.id}
                          className={`text-xs px-2.5 py-0.5 rounded-sm border font-medium backdrop-blur-sm ${tagColor(tag.name)}`}
                        >
                          {FILTER_LABELS[tag.name] || tag.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Text */}
                  <div className="p-5 border-t border-zinc-800">
                    <h3
                      className="proj-title text-white text-2xl mb-2 tracking-wide group-hover:text-amber-400 transition-colors duration-200"
                      style={{ fontWeight: 700 }}
                    >
                      {project.title}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3">
                      {project.description}
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 text-amber-500 text-xs font-semibold tracking-widest uppercase cursor-pointer hover:text-amber-400 transition-colors w-fit">
                      <span>View Project</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}
